import React from 'react';
import { useTranslation } from 'react-i18next';

import { Panel, SectionTitle, Button, Modal, Text } from '../../../../components';
// The CTA reuses Account's red "Log out of all devices" control (via DeleteRequestButton) and its
// footer layout as-is, so the two danger actions in Settings look exactly alike.
import { Footer } from '../AccountSection/AccountSection.styles';
import {
  DeleteRequestButton,
  Body,
  Copy,
  Subheading,
  BulletList,
  BulletItem,
  ErrorSlot,
  ErrorText,
} from './DeleteAccountSection.styles';

export type DeleteAccountSectionProps = {
  /** The owner's sign-in email, named in the confirmation popup; null (or empty) when unknown. */
  email: string | null;
  /** Sends the deletion request. Resolves when the backend accepted it, rejects on any failure. */
  onRequest: () => Promise<void>;
};

type Status = 'idle' | 'sending' | 'sent' | 'error';

/**
 * Settings > Profile, "Delete your account". Explains what is deleted and kept and that a person on
 * the data protection team handles every request, then sends the request on the first click of the
 * red CTA (no confirm step, by product decision: the request deletes nothing by itself). On success a
 * popup confirms it was sent; closing it returns the card to idle. On failure a translated line shows
 * under the CTA (never the raw backend message) and the CTA can be clicked again.
 */
export const DeleteAccountSection = ({ email, onRequest }: DeleteAccountSectionProps): React.ReactElement => {
  const { t } = useTranslation();
  const [status, setStatus] = React.useState<Status>('idle');
  // The real repeat guard: the busy CTA is only aria-disabled (it stays clickable so it keeps focus),
  // and a second click landing before the re-render must not send twice.
  const inFlight = React.useRef(false);
  const sending = status === 'sending';

  const send = React.useCallback(async (): Promise<void> => {
    if (inFlight.current) return;
    inFlight.current = true;
    setStatus('sending');
    try {
      await onRequest();
      setStatus('sent');
    } catch {
      setStatus('error');
    } finally {
      inFlight.current = false;
    }
  }, [onRequest]);

  const onClick = React.useCallback((): void => {
    if (sending) return;
    void send();
  }, [sending, send]);

  // Every way out of the popup (footer Close, the X, Escape, the backdrop) lands here.
  const onClose = React.useCallback((): void => setStatus('idle'), []);

  return (
    <>
      <Panel>
        <SectionTitle title={t('Delete your account')} icon="trash" />
        <Body>
          <Copy
            text="Ask us to delete your Whitelabels.com account and the personal data linked to it."
            tag="paragraph"
          />
          <Subheading text="What we delete" tag="h3" />
          <BulletList>
            <BulletItem>
              <Text text="Your profile: name, company, address and sign-in email" tag="span" />
            </BulletItem>
            <BulletItem>
              <Text
                text="How you sign in: password, linked Google or Telegram accounts and active sessions"
                tag="span"
              />
            </BulletItem>
            <BulletItem>
              <Text text="Your settings and saved preferences" tag="span" />
            </BulletItem>
          </BulletList>
          <Copy
            text="We keep invoices and payment records for as long as tax and accounting laws require (typically 5 to 7 years)."
            tag="paragraph"
          />
          <Copy
            text="Nothing is deleted automatically. A member of our data protection team reviews every request, may ask you to confirm your identity, and emails you at each step."
            tag="paragraph"
          />
        </Body>
        <Footer>
          {/* aria-disabled, not disabled: the button keeps focus while sending, so closing the
              popup returns focus here; onClick (sending check + inFlight ref) blocks repeats. */}
          <DeleteRequestButton type="button" onClick={onClick} aria-disabled={sending} aria-busy={sending}>
            {sending ? (
              <Text text="Sending request…" tag="span" />
            ) : (
              <Text text="Request account deletion" tag="span" />
            )}
          </DeleteRequestButton>
          {status === 'error' ? (
            <ErrorSlot role="alert">
              <ErrorText text="We couldn't send your request. Please try again." tag="span" />
            </ErrorSlot>
          ) : null}
        </Footer>
      </Panel>

      <Modal
        open={status === 'sent'}
        title={t('Request sent')}
        onClose={onClose}
        footer={<Button label={t('Close')} onClick={onClose} />}
      >
        {email ? (
          <Copy
            text="We received your account deletion request and sent a confirmation to {{email}}. Our data protection team will review it and email you with the next steps."
            placeholders={{ email }}
            tag="paragraph"
          />
        ) : (
          <Copy
            text="We received your account deletion request and emailed you a confirmation. Our data protection team will review it and email you with the next steps."
            tag="paragraph"
          />
        )}
      </Modal>
    </>
  );
};

export default DeleteAccountSection;
